import { Controller } from '@hotwired/stimulus'
import consumer from '~/channels/consumer'

export default class extends Controller {
  static targets = ['messages', 'replyContent', 'attachmentPreview', 'survey']

  static values = {
    ticketId: String,
    channel: String
  }

  connect() {
    this.subscribeToTicketUpdates()
    this.scrollToBottom()
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  subscribeToTicketUpdates() {
    this.subscription = consumer.subscriptions.create(
      { channel: 'SupportTicketChannel', ticket_id: this.ticketIdValue },
      {
        connected: () => {
          console.log('Connected to SupportTicketChannel')
        },

        disconnected: () => {
          console.log('Disconnected from SupportTicketChannel')
        },

        received: (data) => {
          this.handleTicketUpdate(data)
        }
      }
    )
  }

  handleTicketUpdate(data) {
    const { event } = data

    switch (event) {
      case 'new_message':
        this.appendMessage(data.message)
        break
      case 'ticket_closed':
        this.handleTicketClosed()
        break
      case 'ticket_reopened':
        this.handleTicketReopened()
        break
      default:
        console.log('Unknown ticket event:', event, data)
    }
  }

  appendMessage(message) {
    if (!this.hasMessagesTarget) return

    // Check if we have an empty state and remove it
    const emptyState = this.messagesTarget.querySelector('.text-center.py-8')
    if (emptyState) {
      emptyState.remove()
    }

    // Build HTML from JSON message data
    const messageHtml = this.buildMessageHtml(message)
    this.messagesTarget.insertAdjacentHTML('beforeend', messageHtml)

    this.scrollToBottom()
  }

  buildMessageHtml(message) {
    const isFromUser = message.message_type === 'user'
    const alignClass = isFromUser ? 'justify-end' : 'justify-start'
    const bubbleClass = isFromUser ?
      'bg-gray-900 text-white' :
      'bg-gray-100 border-2 border-gray-300'
    const avatarClass = isFromUser ?
      'bg-white text-gray-900' :
      'bg-gray-900 text-white'
    const initials = isFromUser ? 'You' : 'ST'
    const senderName = isFromUser ? 'You' : 'Support Team'

    const createdAt = new Date(message.created_at)
    const timeStr = createdAt.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })

    let attachmentsHtml = ''
    if (message.attachments && message.attachments.length > 0) {
      const images = message.attachments.map((a) => `<a href="${a.url}" target="_blank" class="block">
          <img src="${a.url}" class="h-20 w-20 object-cover border ${isFromUser ? 'border-gray-600' : 'border-gray-300'}">
        </a>`).join('')
      attachmentsHtml = `<div class="mt-3 flex flex-wrap gap-2">${images}</div>`
    }

    return `
      <div class="flex ${alignClass}">
        <div class="max-w-[80%]">
          <div class="${bubbleClass} px-4 py-3">
            <div class="flex items-center space-x-2 mb-2">
              <div class="w-6 h-6 ${avatarClass} border border-gray-500 flex items-center justify-center text-xs font-bold">
                ${isFromUser ? initials.charAt(0) : 'ST'}
              </div>
              <span class="text-xs font-semibold">${senderName}</span>
            </div>
            <div class="text-sm whitespace-pre-wrap">${this.escapeHtml(message.content || '')}</div>
            ${attachmentsHtml}
          </div>
          <div class="text-xs text-gray-400 mt-1 ${isFromUser ? 'text-right' : 'text-left'}">
            ${timeStr}
          </div>
        </div>
      </div>
    `
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  scrollToBottom() {
    if (this.hasMessagesTarget) {
      this.messagesTarget.scrollTop = this.messagesTarget.scrollHeight
    }
  }

  submitReply(event) {
    event.preventDefault()

    const form = event.target
    const content = this.replyContentTarget?.value?.trim()

    if (!content) {
      this.showNotification('Please enter a message', 'error')
      return
    }

    const formData = new FormData(form)

    fetch(form.action, {
      method: 'POST',
      body: formData,
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
        'Accept': 'application/json'
      }
    })
      .then((response) => {
        if (!response.ok) throw new Error('Failed to send message')
        return response.json()
      })
      .then((_data) => {
        // Clear the form
        if (this.hasReplyContentTarget) {
          this.replyContentTarget.value = ''
        }
        if (this.hasAttachmentPreviewTarget) {
          this.attachmentPreviewTarget.innerHTML = ''
        }

        // Message will be appended via ActionCable broadcast
        this.showNotification('Message sent', 'success')
      })
      .catch((error) => {
        console.error('Error sending message:', error)
        this.showNotification('Failed to send message. Please try again.', 'error')
      })
  }

  handleAttachments(event) {
    const files = event.target.files
    if (!this.hasAttachmentPreviewTarget) return

    // Clear existing previews
    this.attachmentPreviewTarget.innerHTML = ''

    if (files.length > 3) {
      this.showNotification('Maximum 3 images allowed', 'error')
      event.target.value = ''
      return
    }

    Array.from(files).forEach((file) => {
      if (!file.type.startsWith('image/')) return

      const preview = document.createElement('span')
      preview.className = 'inline-flex items-center px-2 py-1 bg-gray-100 border text-xs'
      preview.textContent = file.name.length > 20 ? `${file.name.substring(0, 20)}...` : file.name
      this.attachmentPreviewTarget.appendChild(preview)
    })
  }

  handleTicketClosed() {
    // Reload the page to show closed state
    this.showNotification('Ticket has been closed', 'success')
    setTimeout(() => window.location.reload(), 1500)
  }

  handleTicketReopened() {
    // Reload the page to show open state
    this.showNotification('Ticket has been reopened', 'success')
    setTimeout(() => window.location.reload(), 1500)
  }

  showNotification(message, type) {
    const notification = document.createElement('div')
    notification.className = `fixed top-4 right-4 z-50 p-4 border-2 ${
      type === 'success' ?
        'bg-green-50 border-green-500 text-green-700' :
        'bg-red-50 border-red-500 text-red-700'
    }`
    notification.textContent = message

    document.body.appendChild(notification)

    setTimeout(() => {
      notification.remove()
    }, 3000)
  }
}
